import { ColumnDef } from '@tanstack/react-table';
import { Badge } from 'components/ui/badge';
import { DataTableColumnHeader } from '../../../../components/blocks/data-table/data-table-column-header';
import { IamData } from '../../services/user-service';
import { DataTableRowActions } from './iam-table-row-actions';
import { ORGANIZATION_CONSTANTS } from '../../../../constant/organization';
import { Copy } from 'lucide-react';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';

// Group Dialog Component
const GroupsDialog = ({ groups, open, onOpenChange, t }: {
  groups: Array<{ name: string }>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  t: (key: string) => string;
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>{t('ALL_GROUPS')}</DialogTitle>
        </DialogHeader>
        <div
          className="space-y-2 max-h-[400px] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {groups.map((group, index) => (
            <div
              key={index}
              className="p-3 rounded-md border bg-muted/50"
              onClick={(e) => e.stopPropagation()}
            >
              {group.name}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Group Cell Component
const GroupCell = ({ groups, t }: {
  groups: Array<{ name: string }> | undefined;
  t: (key: string) => string;
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (!groups || groups.length === 0) {
    return <span>-</span>;
  }

  if (groups.length === 1) {
    return <span className="max-w-[200px] truncate">{groups[0].name}</span>;
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          variant="link"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setIsDialogOpen(true);
          }}
          className="h-auto p-0 text-xs text-primary hover:underline"
        >
          {t('VIEW_ALL')} ({groups.length})
        </Button>
      </div>
      <GroupsDialog
        groups={groups}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        t={t}
      />
    </>
  );
};

type StatusOption = {
  value: string | boolean;
  label: string;
  color: 'success' | 'error';
};

const getUserStatuses = (t: (key: string) => string): StatusOption[] => [
  { value: 'active', label: t('ACTIVE'), color: 'success' },
  { value: 'inactive', label: t('INACTIVE'), color: 'error' },
];

const getMfaStatuses = (t: (key: string) => string): StatusOption[] => [
  { value: true, label: t('ENABLED'), color: 'success' },
  { value: false, label: t('DISABLED'), color: 'error' },
];

interface ColumnFactoryProps {
  onViewDetails: (user: IamData) => void;
  onResetPassword: (user: IamData) => void;
  onResendActivation?: (user: IamData) => void;
  onDeactivateUser?: (user: IamData) => void;
  onChangePlan?: (user: IamData) => void;
  onCancelSubscription?: (user: IamData) => void;
  onCopyEmail?: (email: string) => void;
  t: (key: string) => string;
}

export const createIamTableColumns = ({
  onViewDetails,
  onResetPassword,
  onResendActivation,
  onDeactivateUser,
  onChangePlan,
  onCancelSubscription,
  onCopyEmail,
  t
}: ColumnFactoryProps): ColumnDef<IamData, any>[] => [
    {
      id: 'fullName',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('NAME')} />,
      accessorFn: (row) => {
        const fullName = `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim();
        return fullName || '-';
      },
      cell: ({ row }) => {
        const fullName = `${row.original.firstName ?? ''} ${row.original.lastName ?? ''}`.trim();
        return (
          <div className="flex items-center">
            <span className="max-w-[300px] truncate font-medium">{fullName || '-'}</span>
          </div>
        );
      },
    },
    {
      id: 'email',
      accessorFn: (row) => row.email,
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMAIL')} />,
      cell: ({ row }) => (
        <div className="flex items-center gap-2 group">
          <span className="max-w-[300px] truncate">{row.original.email}</span>
          {onCopyEmail && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onCopyEmail(row.original.email);
              }}
              className="flex items-center justify-center h-6 w-6 p-1 rounded hover:bg-muted transition-colors opacity-0 group-hover:opacity-100"
              title={t('COPY_EMAIL')}
            >
              <Copy size={12} className="text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>
      ),
    },
    {
      id: 'organization',
      accessorFn: (row) => {
        if (!row.organizations || row.organizations.length === 0) return '-';
        if (row.organizations[0].itemId === ORGANIZATION_CONSTANTS.DEFAULT_ORGANIZATION_ID || row.organizations[0].name === ORGANIZATION_CONSTANTS.DEFAULT_ORGANIZATION_NAME) return '-';
        return row.organizations[0].name;
      },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('ORGANIZATION')} />,
      cell: ({ row }) => {
        const organization = (() => {
          if (!row.original.organizations || row.original.organizations.length === 0) return '-';
          if (row.original.organizations[0].itemId === ORGANIZATION_CONSTANTS.DEFAULT_ORGANIZATION_ID || row.original.organizations[0].name === ORGANIZATION_CONSTANTS.DEFAULT_ORGANIZATION_NAME) return '-';
          return row.original.organizations[0].name;
        })();

        return (
          <div className="flex items-center">
            <span className="max-w-[200px] truncate">{organization}</span>
          </div>
        );
      },
    },
    {
      id: 'group',
      accessorFn: (row) => {
        if (!row.groups || row.groups.length === 0) return '-';
        return row.groups[0].name;
      },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('GROUP')} />,
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            <GroupCell groups={row.original.groups} t={t} />
          </div>
        );
      },
    },
    {
      id: 'plan',
      accessorFn: (row) => row.plan?.name,
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('PLAN')} />,
      cell: ({ row }) => {
        const planName = row.original.plan?.name ? t(row.original.plan.name) : '-';
        return (
          <div className="flex items-center">
            <span className="max-w-[150px] truncate">{planName}</span>
          </div>
        );
      },
    },
    {
      id: 'mfaEnabled',
      accessorFn: (row) => row.mfaEnabled,
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('MFA')} />,
      cell: ({ row }) => {
        const mfaStatus = getMfaStatuses(t).find((status) => status.value === row.original.mfaEnabled);
        if (!mfaStatus) return null;
        return <div className="flex items-center">{mfaStatus.label}</div>;
      },
      filterFn: (row, id, value: string[]) => {
        if (value.length === 0) return true;
        const cellValue = row.getValue(id);
        return value.includes(String(cellValue));
      },
    },
    {
      id: 'createdDate',
      accessorFn: (row) => row.createdDate,
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('JOINED_ON')} />,
      cell: ({ row }) => {
        const date = new Date(row.original.createdDate);
        return (
          <div className="flex items-center">
            <span>
              {new Date(date)
                .toLocaleDateString('de-CH', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
                .split('/')
                .join('/')}
            </span>
          </div>
        );
      },
      sortingFn: (rowA, rowB) => {
        const a = new Date(rowA.original.createdDate).getTime();
        const b = new Date(rowB.original.createdDate).getTime();
        return a < b ? -1 : a > b ? 1 : 0;
      },
      filterFn: (row, id, value) => {
        if (!value?.from || !value?.to) return true;
        const rowDate = new Date(row.getValue(id));
        return rowDate >= value.from && rowDate <= value.to;
      },
    },
    {
      id: 'lastLoggedInTime',
      accessorFn: (row) => row.lastLoggedInTime,
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('LAST_LOGIN')} />,
      cell: ({ row }) => {
        const date = new Date(row.original.lastLoggedInTime);
        if (date.getFullYear() === 1) {
          return <div className="text-muted-foreground">-</div>;
        }
        return (
          <div>
            <span>
              {new Date(date)
                .toLocaleDateString('de-CH', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
                .split('/')
                .join('/')}
            </span>
          </div>
        );
      },
      sortingFn: (rowA, rowB) => {
        const a = new Date(rowA.original.lastLoggedInTime).getTime();
        const b = new Date(rowB.original.lastLoggedInTime).getTime();
        return a < b ? -1 : a > b ? 1 : 0;
      },
      filterFn: (row, id, value) => {
        if (!value?.from || !value?.to) return true;
        const rowDate = new Date(row.getValue(id));
        return rowDate >= value.from && rowDate <= value.to;
      },
    },
    {
      id: 'active',
      accessorFn: (row) => row.active,
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
      cell: ({ row }) => {
        const status = getUserStatuses(t).find(
          (status) => status.value === (row.original.active ? 'active' : 'inactive')
        );
        if (!status) return null;
        return (
          <div className="flex items-center">
            <Badge
              variant="outline"
              className={status.color === 'success' ? 'text-success' : 'text-error'}
            >
              {status.label}
            </Badge>
          </div>
        );
      },
      sortingFn: (rowA, rowB, columnId) => {
        const a = rowA.getValue(columnId);
        const b = rowB.getValue(columnId);

        if (b === a) return 0;
        return a ? -1 : 1;
      },
      filterFn: (row, id, value: string[]) => {
        if (value.length === 0) return true;
        const cellValue = row.getValue(id);
        return value.includes(String(cellValue));
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DataTableRowActions
          row={row}
          onViewDetails={onViewDetails}
          onResetPassword={onResetPassword}
          onResendActivation={onResendActivation}
          onDeactivateUser={onDeactivateUser}
          onChangePlan={onChangePlan}
          onCancelSubscription={onCancelSubscription}
        />
      ),
    },
  ];